// Regelauswertung innerhalb eines Zeitplans (Pflichtenheft 7.2, DQ-SCH-002/003).
import { WEEKDAYS, addDays, daysBetween, formatDate, fromLocalAll, isValidTimeZone, parseDate, parseTime, toLocal, weekdayOf, } from './time.js';
const TYPE_RANK = { single_date: 0, date_range: 1, weekly: 2 };
function windowOf(rule) {
    const from = rule.timeFrom == null ? 0 : parseTime(rule.timeFrom);
    const until = rule.timeUntil == null ? 86400 : parseTime(rule.timeUntil);
    if (from == null || until == null || from >= 86400)
        return null;
    return { from, until, overnight: until <= from };
}
function dateBounds(rule) {
    if (!rule.dateFrom)
        return null;
    const from = parseDate(rule.dateFrom);
    const until = parseDate(rule.dateUntil ?? rule.dateFrom);
    if (!from || !until)
        return null;
    return { from, until };
}
/** Gilt die Regel für einen Tag, an dem ein Zeitfenster beginnt? */
function dayMatches(rule, d) {
    if (rule.type === 'weekly') {
        const days = rule.weekdays ?? [];
        return days.includes(WEEKDAYS[weekdayOf(d)]);
    }
    const b = dateBounds(rule);
    if (!b)
        return false;
    const s = formatDate(d);
    return s >= formatDate(b.from) && s <= formatDate(b.until);
}
/** Deckt die Regel die lokale Wanduhrzeit ab? (Schritt 1) */
export function ruleCovers(rule, local) {
    if (rule.enabled === false)
        return false;
    const w = windowOf(rule);
    if (!w)
        return false;
    const secs = local.hour * 3600 + local.minute * 60 + local.second;
    const today = { year: local.year, month: local.month, day: local.day };
    if (!w.overnight)
        return secs >= w.from && secs < w.until && dayMatches(rule, today);
    // Über Mitternacht: der Wochentag/das Datum bezieht sich auf den Start.
    if (secs >= w.from && dayMatches(rule, today))
        return true;
    return secs < w.until && dayMatches(rule, addDays(today, -1));
}
/** Länge des Zeitfensters in Sekunden über die gesamte Geltung (Schritt 4: kürzer = spezifischer). */
export function ruleSpan(rule) {
    const w = windowOf(rule);
    if (!w)
        return Infinity;
    const daily = w.overnight ? 86400 - w.from + w.until : w.until - w.from;
    let days;
    if (rule.type === 'weekly')
        days = new Set(rule.weekdays ?? []).size;
    else {
        const b = dateBounds(rule);
        days = b ? daysBetween(b.from, b.until) + 1 : 0;
    }
    return daily * days;
}
/** Konfliktauflösung (Schritte 2–5). Negativ = a gewinnt. */
export function compareRules(a, b) {
    if (a.priority !== b.priority)
        return b.priority - a.priority;
    const ta = TYPE_RANK[a.type];
    const tb = TYPE_RANK[b.type];
    if (ta !== tb)
        return ta - tb;
    const sa = ruleSpan(a);
    const sb = ruleSpan(b);
    if (sa !== sb)
        return sa < sb ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
export function candidatesAt(schedule, at, timeZone) {
    const local = toLocal(at, timeZone);
    return schedule.rules.filter((r) => ruleCovers(r, local)).sort(compareRules);
}
/** Wirksame Playlist zum Zeitpunkt `at` in der Zeitzone des Standorts (Kap. 7.2). */
export function evaluateSchedule(schedule, at, timeZone, opts = {}) {
    const ms = typeof at === 'number' ? at : at.getTime();
    const local = toLocal(ms, timeZone);
    const candidates = schedule.rules.filter((r) => ruleCovers(r, local)).sort(compareRules);
    const available = (id) => !opts.isPlaylistAvailable || opts.isPlaylistAvailable(id, ms);
    const rule = candidates[0] ?? null;
    if (rule && available(rule.playlist))
        return { playlist: rule.playlist, source: 'rule', rule, candidates, local };
    const fb = schedule.fallbackPlaylist;
    if (fb && available(fb))
        return { playlist: fb, source: 'fallback', rule, candidates, local };
    return { playlist: null, source: 'none', rule, candidates, local };
}
/**
 * Nächster Zeitpunkt > at, an dem sich die Kandidatenmenge ändern kann (Regelgrenze).
 * Die Engine setzt darauf einen Timer. null = keine Regeln.
 */
export function nextBoundary(schedule, at, timeZone) {
    const ms = typeof at === 'number' ? at : at.getTime();
    const rules = schedule.rules.filter((r) => r.enabled !== false);
    if (!rules.length)
        return null;
    const times = new Set([0]);
    const dates = new Map();
    const addDate = (d) => void dates.set(formatDate(d), d);
    const local = toLocal(ms, timeZone);
    for (let i = -1; i <= 8; i++)
        addDate(addDays(local, i));
    for (const r of rules) {
        const w = windowOf(r);
        if (w) {
            times.add(w.from);
            if (w.until < 86400)
                times.add(w.until);
        }
        const b = dateBounds(r);
        if (b) {
            addDate(b.from);
            addDate(addDays(b.until, 1));
            addDate(addDays(b.until, 2));
        }
    }
    let best = null;
    for (const d of dates.values()) {
        for (const s of times) {
            for (const t of fromLocalAll(d, s, timeZone))
                if (t > ms && (best === null || t < best))
                    best = t;
        }
    }
    return best;
}
/** „Was läuft wann" (Kap. 7.3): Abschnitte zwischen from und until. */
export function simulate(schedule, from, until, timeZone, opts = {}) {
    const end = typeof until === 'number' ? until : until.getTime();
    let t = typeof from === 'number' ? from : from.getTime();
    const out = [];
    for (let guard = 0; t < end && guard < 100000; guard++) {
        const ev = evaluateSchedule(schedule, t, timeZone, opts);
        const nb = nextBoundary(schedule, t, timeZone);
        const segEnd = nb === null ? end : Math.min(nb, end);
        const last = out[out.length - 1];
        const ruleId = ev.source === 'rule' ? (ev.rule?.id ?? null) : null;
        if (last && last.playlist === ev.playlist && last.source === ev.source && last.ruleId === ruleId)
            last.until = segEnd;
        else
            out.push({ from: t, until: segEnd, playlist: ev.playlist, source: ev.source, ruleId });
        t = segEnd;
    }
    return out;
}
/** Strukturprüfung für Manager/Server (422 vor dem Veröffentlichen). */
export function validateRule(rule) {
    const issues = [];
    const add = (field, message) => void issues.push({ ruleId: rule.id, field, message });
    if (!Number.isInteger(rule.priority) || rule.priority < 0 || rule.priority > 100)
        add('priority', 'Priorität muss eine ganze Zahl von 0 bis 100 sein');
    if (!(rule.type in TYPE_RANK))
        add('type', `Unbekannter Regeltyp ${String(rule.type)}`);
    if (rule.timeFrom != null && (parseTime(rule.timeFrom) ?? 86400) >= 86400)
        add('timeFrom', 'Ungültige Uhrzeit');
    if (rule.timeUntil != null && parseTime(rule.timeUntil) == null)
        add('timeUntil', 'Ungültige Uhrzeit');
    if (rule.type === 'weekly') {
        for (const d of rule.weekdays ?? [])
            if (!WEEKDAYS.includes(d))
                add('weekdays', `Unbekannter Wochentag ${d}`);
    }
    else {
        if (!rule.dateFrom || !parseDate(rule.dateFrom))
            add('dateFrom', 'Datum fehlt oder ist ungültig');
        if (rule.dateUntil != null && !parseDate(rule.dateUntil))
            add('dateUntil', 'Ungültiges Datum');
        if (rule.dateFrom && rule.dateUntil && rule.dateUntil < rule.dateFrom)
            add('dateUntil', 'Ende liegt vor Beginn');
        if (rule.type === 'single_date' && rule.dateUntil != null && rule.dateUntil !== rule.dateFrom)
            add('dateUntil', 'single_date hat nur ein Datum');
    }
    return issues;
}
export function validateSchedule(schedule, timeZone) {
    const issues = schedule.rules.flatMap(validateRule);
    const ids = new Set();
    for (const r of schedule.rules) {
        if (ids.has(r.id))
            issues.push({ ruleId: r.id, field: 'id', message: 'Doppelte Regel-ID' });
        ids.add(r.id);
    }
    if (timeZone !== undefined && !isValidTimeZone(timeZone))
        issues.push({ ruleId: '', field: 'timezone', message: `Unbekannte Zeitzone ${timeZone}` });
    return issues;
}
//# sourceMappingURL=rules.js.map